// Email validation regex pattern
const EMAIL_VALIDATION_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/**
 * Media queries mirroring Tailwind's default screens, so JS switches at exactly the same width as the
 * CSS it pairs with. BELOW_MD is the complement of `md:` (min-width 768px); BELOW_LG of `lg:` (1024px).
 * The .98 avoids the 1px overlap a `max-width: 768px` query has with `min-width: 768px`.
 * Security: static strings, no user input.
 */
const MEDIA_QUERY = {
  BELOW_MD: '(max-width: 767.98px)',
  BELOW_LG: '(max-width: 1023.98px)',
};

/**
 * Subscribes to a media query and calls back whenever it starts or stops matching (resize, rotation,
 * zoom). Uses the query's own change event, so no resize polling is needed.
 * @param {string} query - Media query string, normally a MEDIA_QUERY value.
 * @param {(matches: boolean) => void} callback - Called with the new match state on every change.
 * @returns {MediaQueryList} The live query list; read `.matches` for the current state.
 */
function watchMediaQuery(query, callback) {
  const mediaQueryList = window.matchMedia(query);
  const handleChange = (event) => callback(event.matches);

  if (typeof mediaQueryList.addEventListener === 'function') {
    mediaQueryList.addEventListener('change', handleChange);
  } else {
    // Safari < 14 only supports the legacy listener API.
    mediaQueryList.addListener(handleChange);
  }

  return mediaQueryList;
}

let bodyScrollLockPosition = null;

/**
 * Locks page scrolling behind a drawer or modal while keeping the current scroll position.
 * `body.modal-open` is `position: fixed`, which would otherwise jump the page to the top, so the body is
 * offset by the saved scroll position and restored on unlock. Calling it while already locked is a no-op.
 */
function lockBodyScroll() {
  if (bodyScrollLockPosition !== null) return;

  bodyScrollLockPosition = window.scrollY;

  document.body.style.top = `-${bodyScrollLockPosition}px`;
  document.body.classList.add('modal-open');
}

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex="0"]';

/**
 * Keyboard handling for an open modal or drawer: moves focus inside, keeps Tab / Shift+Tab within it,
 * closes on Escape, and hands focus back to whatever was focused before when released.
 * @param {HTMLElement} container - The dialog panel.
 * @param {{ onEscape?: Function }} [options] - Called when Escape is pressed (normally the close handler).
 * @returns {(options?: { restoreFocus?: boolean }) => void} Release function; call it when the dialog closes.
 */
function manageDialogFocus(container, { onEscape } = {}) {
  const previouslyFocused = document.activeElement;

  const getFocusable = () =>
    [...container.querySelectorAll(FOCUSABLE_SELECTOR)].filter((el) => el.offsetParent !== null);

  const handleKeydown = (event) => {
    if (event.key === 'Escape') {
      onEscape?.();

      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusable();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  document.addEventListener('keydown', handleKeydown);
  // Wait a frame so panels revealed by a visibility transition can take focus.
  requestAnimationFrame(() => getFocusable()[0]?.focus());

  return ({ restoreFocus = true } = {}) => {
    document.removeEventListener('keydown', handleKeydown);

    if (restoreFocus && previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
  };
}

/**
 * Releases the lock taken by lockBodyScroll and returns the page to where the user was.
 * Calling it while not locked is a no-op.
 */
function unlockBodyScroll() {
  if (bodyScrollLockPosition === null) return;

  const scrollPosition = bodyScrollLockPosition;
  bodyScrollLockPosition = null;

  document.body.classList.remove('modal-open');
  document.body.style.top = '';

  window.scrollTo({ top: scrollPosition, behavior: 'instant' });
}

/**
 * Generic function to perform fetch requests with enhanced error handling.
 * @param {object} config - Object containing endPoint, method, headers, body, and other fetch options
 * @param {string} config.endPoint - The API endpoint URL
 * @param {string} config.method - HTTP method (GET, POST, etc.)
 * @param {object} config.headers - Request headers
 * @param {string} config.body - Request body
 * @returns {Promise<Response>} - The fetch response promise
 * @throws {Error} - Throws error if request fails
 */
async function fetchData({ endPoint, method = 'GET', headers = {}, body, ...otherOptions }) {
  if (!endPoint) {
    throw new Error('Endpoint is required for fetch request');
  }

  try {
    const response = await fetch(endPoint, {
      method,
      headers,
      body,
      ...otherOptions,
    });

    return response;
  } catch (error) {
    throw new Error(`Fetch while processing the endpoint ${endPoint} error: ${error.message}`);
  }
}

/**
 * Validates an email address using a regular expression.
 * @param {string} email - The email address to validate
 * @returns {boolean} - Returns true if the email is valid, false otherwise
 */
function isEmailValid(email) {
  if (!email || typeof email !== 'string') {
    return false;
  }

  return EMAIL_VALIDATION_REGEX.test(email.trim());
}

/**
 * Displays a toast message with enhanced error handling.
 * @param {string} message - The message to display in the toast
 * @param {number} duration - Duration to show toast in milliseconds (default: 5000)
 */
function showToastMessage(message, duration = 5000) {
  const toast = document.getElementById('errorMessage');
  const messageElement = toast.querySelector('.error-message');

  messageElement.textContent = message;
  toast.classList.remove('hidden', 'opacity-0');

  // Toast layer token from :root (--z-toast); sits above modals and the header.
  toast.style.cssText = 'position: fixed; z-index: var(--z-toast)';

  // Auto-hide toast after specified duration
  const hideTimeout = setTimeout(() => {
    hideToast(toast);
  }, duration);

  // Setup close button listener
  const closeButton = toast.querySelector('button[data-dismiss-target]');

  if (closeButton) {
    const handleCloseClick = function () {
      clearTimeout(hideTimeout);

      hideToast(toast);

      closeButton.removeEventListener('click', handleCloseClick);
    };
    closeButton.addEventListener('click', handleCloseClick);
  }
}

/**
 * Hide toast message utility function
 * @param {HTMLElement} toast - The toast element to hide
 */
function hideToast(toast) {
  if (toast) {
    toast.classList.add('hidden');
    toast.style.cssText = '';
  }
}

/**
 * Sets a value in session storage with error handling.
 * @param {string} key - The key under which to store the value.
 * @param {*} value - The value to store.
 * @returns {boolean} - Returns true if successful, false otherwise.
 */
function setSessionStorageItem(key, value) {
  try {
    if (!key || typeof key !== 'string') {
      console.error('Invalid key provided for session storage');
      return false;
    }

    // Serialize value to JSON string
    const serializedValue = JSON.stringify(value);
    sessionStorage.setItem(key, serializedValue);
    return true;
  } catch (error) {
    console.error('Error setting session storage item:', error);
    return false;
  }
}

/**
 * Retrieves a value from session storage with proper error handling.
 * @param {string} key - The key of the item to retrieve.
 * @param {*} defaultValue - Default value to return if key doesn't exist or parsing fails.
 * @returns {*} - The retrieved value, defaultValue, or null if not found.
 */
function getSessionStorageItem(key, defaultValue = null) {
  try {
    if (!key || typeof key !== 'string') {
      console.error('Invalid key provided for session storage');
      return defaultValue;
    }

    const item = sessionStorage.getItem(key);

    // If the item doesn't exist, return the default value
    if (item === null) {
      return defaultValue;
    }

    return JSON.parse(item);
  } catch (error) {
    console.error('Error retrieving session storage item:', error);
    return defaultValue;
  }
}

/**
 * Deletes an item from session storage with error handling.
 * @param {string} key - The key of the item to delete.
 * @returns {boolean} - Returns true if successful, false otherwise.
 */
function deleteSessionStorageItem(key) {
  try {
    if (!key || typeof key !== 'string') {
      console.error('Invalid key provided for session storage');
      return false;
    }

    // Remove item from session storage
    sessionStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Error deleting session storage item:', error);
    return false;
  }
}

/**
 * Sets a value in local storage.
 * @param {string} key - The key under which to store the value.
 * @param {*} value - The value to store.
 */
function setLocalStorageItem(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

/**
 * Retrieves a value from local storage.
 * @param {string} key - The key of the item to retrieve.
 * @returns {*} - The retrieved value, or null if not found.
 */
function getLocalStorageItem(key) {
  return JSON.parse(localStorage.getItem(key));
}

/**
 * Deletes an item from local storage.
 * @param {string} key - The key of the item to delete.
 */
function deleteLocalStorageItem(key) {
  localStorage.removeItem(key);
}

/**
 * Toggles the visibility of a full-screen preloader with enhanced control.
 * @param {boolean} visible - Whether to show or hide the preloader
 * @param {object} options - Additional options for preloader
 */
function toggleFullScreenPreloader(visible, options = {}) {
  const { loaderId = 'loader', className = 'preloader', hideDelay = 500 } = options;

  let preloader = document.getElementById(loaderId);

  // Show or hide the preloader based on the 'visible' parameter
  if (visible) {
    if (!preloader) {
      preloader = createPreloaderElement(loaderId, className);
      document.body.appendChild(preloader);
    } else {
      preloader.classList.remove('preloader-invisible');
    }

    // Prevent body scrolling
    document.documentElement.style.overflowY = 'hidden';
  } else {
    if (preloader) {
      preloader.classList.add('preloader-invisible');
      setTimeout(() => {
        if (preloader && preloader.parentNode) {
          preloader.remove();
        }
      }, hideDelay);
    }

    // Restore body scrolling
    document.documentElement.style.overflowY = '';
  }
}

/**
 * Create preloader element
 * @param {string} id - Element ID
 * @param {string} className - CSS class name
 * @returns {HTMLElement} - Created preloader element
 */
function createPreloaderElement(id, className) {
  const preloader = document.createElement('div');
  preloader.classList.add(className);
  preloader.id = id;
  preloader.innerHTML = '<div class="preloader-circle"></div>';
  return preloader;
}

/**
 * Authentication Flow Control Utilities
 */

/**
 * Set the current authentication flow state
 * @param {string} state - The flow state to set
 */
function setAuthFlowState(state) {
  setSessionStorageItem(SESSION_KEY.AUTH_FLOW_STATE, state);
}

/**
 * Get the current authentication flow state
 * @returns {string} - The current flow state
 */
function getAuthFlowState() {
  return getSessionStorageItem(SESSION_KEY.AUTH_FLOW_STATE, AUTH_FLOW_STATES.INITIAL);
}

/**
 * Clear the authentication flow state
 */
function clearAuthFlowState() {
  deleteSessionStorageItem(SESSION_KEY.AUTH_FLOW_STATE);
}

/**
 * Check if user can access the verify email screen
 * @returns {boolean} - True if user can access verify email screen
 */
function canAccessVerifyEmail() {
  const flowState = getAuthFlowState();
  const profile = getSessionStorageItem(SESSION_KEY.PROFILE);

  // User can access verify email if they have submitted an email and have profile data
  return flowState === AUTH_FLOW_STATES.EMAIL_SUBMITTED && profile && profile.email;
}

/**
 * Check if user can access the register screen
 * @returns {boolean} - True if user can access register screen
 */
function canAccessRegister() {
  const flowState = getAuthFlowState();
  const profile = getSessionStorageItem(SESSION_KEY.PROFILE);

  // User can access register if they have verified their email and don't have a multipass token
  return flowState === AUTH_FLOW_STATES.REGISTRATION_ALLOWED && profile && profile.email && !profile.multipassToken;
}

/**
 * Redirect user to login page with proper cleanup
 */
function redirectToLogin() {
  clearAuthFlowState();
  deleteSessionStorageItem(SESSION_KEY.PROFILE);
  window.location.replace(PAGE_URLS.SHOPIFY_LOGIN);
}

/**
 * Utility method to set button loading state
 * @param {HTMLElement} button - The button element
 * @param {boolean} isLoading - Whether to set loading state or not
 */
function setButtonLoading(button, isLoading) {
  if (!button) return;

  button.disabled = isLoading;
  // aria-busy tells assistive tech the control is working, not just disabled.
  button.setAttribute('aria-busy', String(Boolean(isLoading)));
  if (isLoading) {
    button.classList.add('loading');
  } else {
    button.classList.remove('loading');
  }
}

/**
 * Escapes a value for safe interpolation into HTML text or a quoted attribute.
 * Security: use for any API/user-supplied string inserted via innerHTML/insertAdjacentHTML (prevents XSS).
 * @param {*} value - Value to escape; null/undefined become an empty string.
 * @returns {string} HTML-escaped string.
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Whether the user has asked the OS to minimise non-essential motion. CSS handles transitions and keyframes
 * globally (input.css); use this for JS-driven motion such as smooth scrolling.
 * @returns {boolean} True when `prefers-reduced-motion: reduce` matches.
 */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Scroll behavior that respects the reduced-motion preference.
 * @returns {ScrollBehavior} 'auto' when reduced motion is preferred, otherwise 'smooth'.
 */
function getScrollBehavior() {
  return prefersReducedMotion() ? 'auto' : 'smooth';
}

/**
 * Loads a classic script once and resolves after it has executed. Repeat calls for the same URL share one
 * promise, so the file is never injected (or executed) twice. Callers that need several files in order should
 * chain the calls.
 * @param {string} src - Script URL.
 * @returns {Promise<void>} Resolves on load; rejects (and allows a retry) on network error.
 */
function loadScriptOnce(src) {
  window.loadedScriptPromises = window.loadedScriptPromises || {};

  if (!window.loadedScriptPromises[src]) {
    window.loadedScriptPromises[src] = new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = src;
      script.onload = () => resolve();

      script.onerror = () => {
        delete window.loadedScriptPromises[src];
        script.remove();

        reject(new Error(`Failed to load ${src}`));
      };

      document.head.appendChild(script);
    });
  }

  return window.loadedScriptPromises[src];
}

/**
 * Fetches the locations feed once per page and shares the parsed list. Used by dynamic-values.js
 * ([location_count]) and locations-services.js (map), which previously downloaded the same file separately.
 * A failed request is not cached, so a later caller can retry.
 * Security: `apiUrl` comes from the `locations-api-url` snippet (fixed per-store URL) via a data-* attribute.
 * @param {string} apiUrl - Locations feed URL.
 * @returns {Promise<Object[]>} All locations (bare array or `{ locations: [...] }` payloads are both accepted).
 */
function fetchLocationsOnce(apiUrl) {
  window.locationsFeedPromises = window.locationsFeedPromises || {};

  if (!window.locationsFeedPromises[apiUrl]) {
    window.locationsFeedPromises[apiUrl] = (async () => {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error(`Failed to load locations (${response.status})`);

      const data = await response.json();
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.locations)) return data.locations;
      throw new Error('Unexpected locations payload');
    })().catch((error) => {
      delete window.locationsFeedPromises[apiUrl];
      throw error;
    });
  }

  return window.locationsFeedPromises[apiUrl];
}

/**
 * Locations shown to customers: Healthpass partners only. The map and every [location_count] use this, so the
 * advertised count matches what the map shows.
 * @param {Object[]} locations - Full locations feed.
 * @returns {Object[]}
 */
function getHealthPassPartnerLocations(locations) {
  return Array.isArray(locations) ? locations.filter((location) => location.isHealthPassPartner) : [];
}

/** * Handles checkout button clicks to initiate the checkout process.
 * Sets a session storage item and redirects to the Shopify login page.
 */
function handleCheckout() {
  const checkoutElements = document.querySelectorAll('button[data-checkout]');

  checkoutElements.forEach(function (checkoutElement) {
    checkoutElement.addEventListener('click', function (event) {
      event.preventDefault();

      // Set session storage items for checkout process and return URL
      setSessionStorageItem(SESSION_KEY.CHECKOUT, true);
      setSessionStorageItem(SESSION_KEY.RETURN_TO, PAGE_URLS.CART);

      // Redirect to Shopify login page for authentication
      window.location.replace(PAGE_URLS.SHOPIFY_LOGIN);
    });
  });
}

/**
 * Initializes the accordion functionality.
 * Toggles the `aria-expanded` attribute of buttons to expand/collapse items.
 * @param {string} selector - The CSS selector for the accordion buttons.
 */
function initializeAccordion(selector) {
  const accordionItems = document.querySelectorAll(selector);

  // Add click event listeners to toggle aria-expanded attribute
  accordionItems.forEach((item) => {
    item.addEventListener('click', function () {
      // Toggle the aria-expanded attribute
      const isExpanded = this.getAttribute('aria-expanded') === 'true';

      // Set to opposite value
      this.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
    });
  });
}

/**
 * Extracts only the hash fragment from a link href.
 * Menu links (e.g. the account menu) may come back as "#billing-payments", "/account#billing-payments"
 * or, inside a market subfolder, "/en-ca#billing-payments". Reading just the hash keeps account-tab
 * navigation independent of the path Shopify localizes onto the link.
 *
 * @param {string} url - A hash, full URL or root-relative path.
 * @returns {string} The hash including the leading "#", or '' when there is none.
 */
function toHash(url) {
  if (!url) return '';

  try {
    // Security: parsed against the storefront origin purely to read the fragment; nothing is navigated here.
    return new URL(url, BASE_URL).hash;
  } catch (error) {
    const index = url.indexOf('#');
    return index === -1 ? '' : url.slice(index);
  }
}

document.addEventListener('DOMContentLoaded', function () {
  // Setup checkout button handlers
  handleCheckout();

  // Initialize accordion for elements with the class 'accordion'
  initializeAccordion('.accordion button');
});
